
import { Navigate, useLocation } from "react-router-dom";
import { Loader } from "lucide-react";
import { useEffect, useState } from "react";
import { isAuthenticated } from "@/integrations/supabase/client";
import { useProviderAuth } from "@/hooks/useProviderAuth";

interface SecureAuthGateProps {
  children: React.ReactNode;
  requireAuth?: boolean;
}

export default function SecureAuthGate({ 
  children, 
  requireAuth = true
}: SecureAuthGateProps) {
  const auth = useProviderAuth();
  const location = useLocation();
  const [authChecked, setAuthChecked] = useState(false);

  // Perform comprehensive authentication check
  useEffect(() => {
    if (auth.isLoaded) {
      const hasProviderSession = Boolean(auth.isSignedIn && auth.user);
      const hasValidAuth = auth.provider === "auth0"
        ? hasProviderSession
        : hasProviderSession && isAuthenticated();
      
      if (requireAuth && !hasValidAuth) {
        console.log('SecureAuthGate: Authentication required but user not properly authenticated', {
          provider: auth.provider,
          isLoaded: auth.isLoaded,
          isSignedIn: auth.isSignedIn,
          hasUser: Boolean(auth.user),
          hasProviderSession,
          path: location.pathname,
        });
      }
      
      setAuthChecked(true);
    }
  }, [auth.isLoaded, auth.isSignedIn, auth.user, location.pathname, requireAuth]);

  // Loading state while authentication is being verified
  if (!auth.isLoaded || !authChecked) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  // Check if authentication is required and valid
  const needsAuth = requireAuth && (
    location.pathname.startsWith("/dashboard") ||
    location.pathname.startsWith("/profile") ||
    location.pathname === "/clerk-demo"
  );

  const hasProviderSession = Boolean(auth.isSignedIn && auth.user);
  const hasValidAuth = auth.provider === "auth0"
    ? hasProviderSession
    : hasProviderSession && isAuthenticated();

  if (needsAuth && !hasValidAuth) {
    // Store current path for redirect after login
    sessionStorage.setItem('redirect_after_login', location.pathname);
    
    console.log('SecureAuthGate: Redirecting unauthenticated user to home', {
      provider: auth.provider,
      isSignedIn: auth.isSignedIn,
      hasUser: Boolean(auth.user),
      path: location.pathname,
    });
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
